import React from "react";
import { toast } from "sonner";
import { Plus, Send, FileSignature, Loader2, CheckCircle2, Clock, Download, Mail } from "lucide-react";
import { adminApi, API } from "../lib/api";

const TYPES = [
  { value: "partnership", label: "Samarbejdsaftale" },
  { value: "subscription", label: "Fast pris (abonnement)" },
  { value: "adhoc", label: "Ad-hoc (engangsbetaling)" },
];

const emptyForm = {
  agreement_type: "partnership",
  customer_company: "", customer_cvr: "", customer_address: "",
  contact_name: "", contact_email: "", contact_phone: "",
  billing_email: "",
  monthly_price: "", total_price: "", price_includes: "",
  service_date: "", start_date: "",
  custom_terms: "",
};

export default function B2BAgreementsPanel() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showForm, setShowForm] = React.useState(false);
  const [form, setForm] = React.useState(emptyForm);
  const [busy, setBusy] = React.useState(false);
  const [busyId, setBusyId] = React.useState(null);

  const load = React.useCallback(async () => {
    try {
      const { data } = await adminApi().get("/admin/b2b-agreements");
      setItems(data || []);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const create = async (e) => {
    e.preventDefault();
    if (!form.customer_company || !form.customer_cvr || !form.contact_name || !form.contact_email) {
      toast.error("Udfyld firma, CVR, kontaktperson og email");
      return;
    }
    setBusy(true);
    try {
      const { data } = await adminApi().post("/admin/b2b-agreements", form);
      if (data?.id) {
        toast.success("Aftale oprettet — klar til at sende");
        setShowForm(false);
        setForm(emptyForm);
        await load();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikke oprette aftale");
    } finally {
      setBusy(false);
    }
  };

  const sendForSigning = async (id, company) => {
    if (!window.confirm(`Send aftalen til underskrift hos ${company}?`)) return;
    setBusyId(id);
    try {
      const { data } = await adminApi().post(`/admin/b2b-agreements/${id}/send-for-signing`);
      if (data?.ok) {
        toast.success("Underskriv-link sendt");
        prompt("Link (gyldigt 30 dage):", data.sign_link);
        await load();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikke sende");
    } finally {
      setBusyId(null);
    }
  };

  const downloadPdf = async (id, company) => {
    try {
      const tk = localStorage.getItem("ema_admin_token");
      const res = await fetch(`${API}/admin/b2b-agreements/${id}/pdf`, { headers: { Authorization: `Bearer ${tk}` } });
      if (!res.ok) { toast.error(`Fejl ${res.status}`); return; }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `b2b-aftale-${company.replace(/\s+/g, "-").toLowerCase()}.pdf`;
      a.click();
    } catch (e) { toast.error(e.message); }
  };

  const emailToMe = async (id, company) => {
    setBusyId(id);
    try {
      const { data } = await adminApi().post(`/admin/b2b-agreements/${id}/email-to-me`);
      if (data?.ok) {
        toast.success(`PDF sendt som vedhæftning til ${data.sent_to}`);
      } else {
        toast.error("Mailen kunne ikke sendes");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <div className="bg-white border border-brand p-6 text-sm">Indlæser B2B-aftaler…</div>;

  return (
    <div className="space-y-4" data-testid="b2b-agreements-panel">
      <div className="flex items-center justify-between">
        <h2 className="font-display font-bold text-2xl text-brand-dark">B2B-aftaler</h2>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="btn-primary flex items-center gap-2 text-sm"
          data-testid="b2b-create-toggle"
        >
          <Plus size={14} /> {showForm ? "Skjul formular" : "Opret ny aftale"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={create} className="bg-white border border-brand p-5 space-y-3" data-testid="b2b-create-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label-flat">Aftaletype</label>
              <select className="input-flat" value={form.agreement_type} onChange={setF("agreement_type")} data-testid="b2b-type">
                {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label-flat">Startdato</label>
              <input className="input-flat" value={form.start_date} onChange={setF("start_date")} placeholder="DD-MM-ÅÅÅÅ" data-testid="b2b-start-date" />
            </div>
            <div>
              <label className="label-flat">Firmanavn *</label>
              <input className="input-flat" value={form.customer_company} onChange={setF("customer_company")} required data-testid="b2b-company" />
            </div>
            <div>
              <label className="label-flat">CVR *</label>
              <input className="input-flat" value={form.customer_cvr} onChange={setF("customer_cvr")} required pattern="[0-9]{8}" maxLength={8} data-testid="b2b-cvr" />
            </div>
            <div className="sm:col-span-2">
              <label className="label-flat">Firmaadresse</label>
              <input className="input-flat" value={form.customer_address} onChange={setF("customer_address")} data-testid="b2b-address" />
            </div>
            <div>
              <label className="label-flat">Kontaktperson *</label>
              <input className="input-flat" value={form.contact_name} onChange={setF("contact_name")} required data-testid="b2b-contact-name" />
            </div>
            <div>
              <label className="label-flat">Email *</label>
              <input type="email" className="input-flat" value={form.contact_email} onChange={setF("contact_email")} required data-testid="b2b-contact-email" />
            </div>
            <div>
              <label className="label-flat">Telefon</label>
              <input className="input-flat" value={form.contact_phone} onChange={setF("contact_phone")} data-testid="b2b-contact-phone" />
            </div>
            <div>
              <label className="label-flat">Faktura-email (hvis anden end ovenfor)</label>
              <input type="email" className="input-flat" value={form.billing_email} onChange={setF("billing_email")} data-testid="b2b-billing-email" />
            </div>

            {form.agreement_type === "subscription" && (
              <div>
                <label className="label-flat">Månedlig pris (DKK)</label>
                <input className="input-flat" value={form.monthly_price} onChange={setF("monthly_price")} placeholder="fx 12.500" data-testid="b2b-monthly-price" />
              </div>
            )}
            {form.agreement_type === "adhoc" && (
              <>
                <div>
                  <label className="label-flat">Total pris (DKK)</label>
                  <input className="input-flat" value={form.total_price} onChange={setF("total_price")} data-testid="b2b-total-price" />
                </div>
                <div>
                  <label className="label-flat">Udførelsesdato</label>
                  <input className="input-flat" value={form.service_date} onChange={setF("service_date")} placeholder="DD-MM-ÅÅÅÅ" data-testid="b2b-service-date" />
                </div>
              </>
            )}
            {(form.agreement_type === "subscription" || form.agreement_type === "adhoc") && (
              <div className="sm:col-span-2">
                <label className="label-flat">Pris inkluderer</label>
                <input className="input-flat" value={form.price_includes} onChange={setF("price_includes")} placeholder="fx Op til 200 leveringer/måned i KBH-området" data-testid="b2b-price-includes" />
              </div>
            )}
            <div className="sm:col-span-2">
              <label className="label-flat">Særlige vilkår (valgfrit)</label>
              <textarea className="input-flat min-h-[80px]" value={form.custom_terms} onChange={setF("custom_terms")} placeholder="Tilføj eventuelle specielle vilkår der gælder for netop denne kunde…" data-testid="b2b-custom-terms" />
            </div>
          </div>
          <button type="submit" disabled={busy} className="btn-primary" data-testid="b2b-create-submit">
            {busy ? <><Loader2 className="animate-spin" size={14}/> Opretter…</> : <>Opret aftale</>}
          </button>
        </form>
      )}

      <div className="bg-white border border-brand overflow-x-auto" data-testid="b2b-list">
        <table className="w-full text-sm">
          <thead className="bg-brand-bg2 text-left text-xs uppercase text-brand-text2">
            <tr>
              <th className="px-4 py-3">Firma</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Kontakt</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Handlinger</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-brand-text2 italic">Ingen B2B-aftaler endnu</td></tr>
            )}
            {items.map((a) => {
              const sig = a.signing;
              const isSigned = sig?.status === "signed";
              const isSent = sig?.status === "sent";
              return (
                <tr key={a.id} className="border-t border-brand" data-testid={`b2b-row-${a.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold">{a.customer_company}</div>
                    <div className="text-xs text-brand-text2">CVR {a.customer_cvr}</div>
                  </td>
                  <td className="px-4 py-3 text-xs">{TYPES.find((t) => t.value === a.agreement_type)?.label || a.agreement_type}</td>
                  <td className="px-4 py-3 text-xs">
                    <div>{a.contact_name}</div>
                    <div className="text-brand-text2">{a.contact_email}</div>
                  </td>
                  <td className="px-4 py-3">
                    {isSigned ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold text-xs"><CheckCircle2 size={12}/> Underskrevet {sig.method === "mitid_erhverv" ? "(MitID)" : "(navn)"}</span>
                    ) : isSent ? (
                      <span className="inline-flex items-center gap-1 text-amber-700 text-xs"><Clock size={12}/> Afventer underskrift</span>
                    ) : (
                      <span className="text-xs text-brand-text2">Udkast</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right space-x-2">
                    {!isSigned && (
                      <button
                        onClick={() => sendForSigning(a.id, a.customer_company)}
                        disabled={busyId === a.id}
                        className="text-xs border border-brand hover:bg-brand-bg2 px-2 py-1 inline-flex items-center gap-1"
                        data-testid={`b2b-send-${a.id}`}
                      >
                        {busyId === a.id ? <Loader2 className="animate-spin" size={11}/> : <Send size={11}/>}
                        {isSent ? "Send igen" : "Send til underskrift"}
                      </button>
                    )}
                    <button
                      onClick={() => emailToMe(a.id, a.customer_company)}
                      disabled={busyId === a.id}
                      className="text-xs border border-brand hover:bg-brand-bg2 px-2 py-1 inline-flex items-center gap-1"
                      data-testid={`b2b-email-${a.id}`}
                      title="Send PDF som vedhæftning til min email"
                    >
                      <Mail size={11}/> Send til mig
                    </button>
                    <button
                      onClick={() => downloadPdf(a.id, a.customer_company)}
                      className="text-xs border border-brand hover:bg-brand-bg2 px-2 py-1 inline-flex items-center gap-1"
                      data-testid={`b2b-pdf-${a.id}`}
                    >
                      <Download size={11}/> Hent
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
